/**
 * AutoScoreBadge — v2.1.0
 *
 * Tiny pill that surfaces the auto-score and its signals on a lead.
 * Hover/tap → tooltip with the breakdown so the commercial understands
 * WHY a lead is HOT without a manual rating.
 *
 * Hidden when auto_score === 0 (avoid visual noise on freshly-imported
 * leads where no signal has fired yet).
 */
import { Sparkles, Wallet, Zap, FileText, Brain } from "lucide-react";
import { AUTO_SIGNAL_LABELS } from "@/lib/crmHelpers";

const SIGNAL_ICONS = {
  budget_high: Wallet,
  reactive: Zap,
  advanced: FileText,
  ai_high: Brain,
};

export default function AutoScoreBadge({ lead, size = "sm" }) {
  const score = lead?.auto_score || 0;
  const signals = lead?.auto_signals || [];
  if (score === 0 || signals.length === 0) return null;

  const sizeCls =
    size === "lg"
      ? "px-2.5 py-1 text-xs"
      : "px-2 py-0.5 text-[10px]";

  return (
    <div className="group relative inline-flex">
      <span
        className={`inline-flex items-center gap-1 rounded-full bg-violet-100 ${sizeCls} font-bold uppercase tracking-wide text-violet-700 dark:bg-violet-950/40 dark:text-violet-300`}
        data-testid="auto-score-badge"
      >
        <Sparkles className="h-3 w-3" />
        Auto · {score}
      </span>

      {/* Tooltip — hover/focus reveals the signal breakdown. Positioned
          to the bottom-right to avoid overlapping the StarScore above. */}
      <div className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden min-w-[200px] rounded-lg border border-border/60 bg-popover px-3 py-2 text-xs shadow-lg group-hover:block">
        <p className="mb-1.5 font-semibold text-foreground">
          Score automatique : {score} / 4
        </p>
        <ul className="space-y-1">
          {signals.map((sig) => {
            const Icon = SIGNAL_ICONS[sig] || Sparkles;
            return (
              <li
                key={sig}
                className="flex items-center gap-1.5 text-muted-foreground"
              >
                <Icon className="h-3 w-3 text-violet-600 flex-none" />
                <span>{AUTO_SIGNAL_LABELS[sig] || sig}</span>
              </li>
            );
          })}
        </ul>
        <p className="mt-2 text-[10px] italic text-muted-foreground">
          Le bucket utilise max(étoiles manuelles, score auto).
        </p>
      </div>
    </div>
  );
}
